import mammoth from 'mammoth'
import { adminApi, getAdminToken } from '../api'
import type { KnowledgeScope } from '../constants/knowledgeScope'

export type AttachmentScopeParams = { scope: KnowledgeScope }

export type FileRec = {
  original_name?: string
  stored_name?: string
  mime?: string
  role?: string
  index?: number
}

function fileExtension(name: string): string {
  const n = (name || '').trim()
  const dot = n.lastIndexOf('.')
  if (dot <= 0 || dot >= n.length - 1) return ''
  return n.slice(dot + 1).toLowerCase()
}

/** 可预览附件列表（排除内嵌图） */
export function listPreviewableFiles(files: FileRec[] | null | undefined): FileRec[] {
  return (files ?? []).filter((f) => f.stored_name && f.role !== 'embedded_figure')
}

/** 在线新建/编辑的正文附件（.md / .html），非 Word/PDF 等上传件 */
export function findOnlineEditableFile(files: FileRec[] | null | undefined): FileRec | null {
  for (const f of listPreviewableFiles(files)) {
    const kind = resolvePreviewKind(f)
    if (kind === 'markdown' || kind === 'html') return f
  }
  return null
}

export function isOnlineEditableDocument(files: FileRec[] | null | undefined): boolean {
  return findOnlineEditableFile(files) != null
}

const PREVIEW_KIND_ORDER: PreviewKind[] = [
  'docx',
  'doc',
  'pdf',
  'markdown',
  'xlsx',
  'image',
  'pptx',
  'unsupported',
]

/** 优先 Word/PDF 主附件，避免多文件条目默认打开 PPT。 */
export function pickPrimaryPreviewFileIndex(files: FileRec[]): number {
  if (!files.length) return 0
  let best = 0
  let bestRank = PREVIEW_KIND_ORDER.length
  let bestUploadIndex = Number.MAX_SAFE_INTEGER
  files.forEach((f, i) => {
    const kind = resolvePreviewKind(f)
    const rank = PREVIEW_KIND_ORDER.indexOf(kind)
    const r = rank >= 0 ? rank : PREVIEW_KIND_ORDER.length
    const uploadIdx = typeof f.index === 'number' ? f.index : i + 1
    if (r < bestRank || (r === bestRank && uploadIdx < bestUploadIndex)) {
      bestRank = r
      bestUploadIndex = uploadIdx
      best = i
    }
  })
  return best
}

/** 优先 original_name 判断类型，避免 stored_name 无后缀时误判。 */
export function resolvePreviewKind(rec: FileRec): PreviewKind {
  const orig = (rec.original_name || '').trim()
  const stored = (rec.stored_name || '').trim()
  if (orig) {
    const fromOrig = previewKind(orig, rec.mime)
    if (fromOrig !== 'unsupported') return fromOrig
  }
  return previewKind(stored, rec.mime)
}

/** 展示用：主文件名 + 后缀（如 .doc） */
export function splitFileDisplayName(full: string): { base: string; ext: string } {
  const name = (full || '').trim() || '文件'
  const i = name.lastIndexOf('.')
  if (i <= 0) return { base: name, ext: '' }
  return { base: name.slice(0, i), ext: name.slice(i) }
}

export type PreviewKind =
  | 'pdf'
  | 'image'
  | 'docx'
  | 'doc'
  | 'pptx'
  | 'xlsx'
  | 'markdown'
  | 'html'
  | 'unsupported'

const MISSING_FILE_HINTS = [
  '404',
  'not found',
  '未找到',
  '无此附件',
  '文件已丢失',
  '文件不存在',
  '无法读取',
]

/** 预览/下载失败时给出可读文案（避免页面只显示 404 或「预览加载失败」） */
export function resolvePreviewLoadError(e: unknown, kind: 'item' | 'file' = 'file'): string {
  const status =
    (e as { httpStatus?: number }).httpStatus ??
    (e as { response?: { status?: number } })?.response?.status
  if (status === 404) {
    return kind === 'item'
      ? '该知识条目不存在或已删除'
      : '文件不存在，请确认服务器上是否仍有该附件或重新上传'
  }
  const raw =
    e instanceof Error ? e.message : typeof e === 'string' ? e : ''
  const m = raw.trim().toLowerCase()
  if (!m || MISSING_FILE_HINTS.some((h) => m.includes(h.toLowerCase()))) {
    return kind === 'item'
      ? '该知识条目不存在或已删除'
      : '文件不存在，请确认服务器上是否仍有该附件或重新上传'
  }
  return raw || '预览加载失败，请稍后重试'
}

export function previewKind(filename: string, mime?: string): PreviewKind {
  const ext = fileExtension(filename)
  const m = (mime || '').toLowerCase()
  if (ext === 'pdf' || m.includes('pdf')) return 'pdf'
  if (ext === 'md' || ext === 'markdown' || m.includes('markdown')) return 'markdown'
  if (ext === 'html' || ext === 'htm' || m.includes('html')) return 'html'
  if (['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'tif', 'tiff'].includes(ext) || m.startsWith('image/'))
    return 'image'
  if (ext === 'docx' || m.includes('wordprocessingml')) return 'docx'
  if (ext === 'doc' || (m.includes('msword') && !m.includes('openxml'))) return 'doc'
  if (ext === 'pptx' || m.includes('presentationml')) return 'pptx'
  if (ext === 'xlsx' || ext === 'xls' || m.includes('spreadsheetml') || m.includes('ms-excel')) return 'xlsx'
  return 'unsupported'
}

const MIME_BY_EXT: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
}

export function guessMimeFromFilename(name: string): string {
  const ext = fileExtension(name)
  return MIME_BY_EXT[ext] || ''
}

async function normalizeResponseBlob(raw: Blob, rec: FileRec, headerMime?: string): Promise<Blob> {
  const fromHeader = (headerMime || '').split(';')[0]?.trim()
  const fromName = guessMimeFromFilename(rec.original_name || rec.stored_name || '')
  const type =
    (fromHeader && fromHeader !== 'application/octet-stream' ? fromHeader : '') ||
    fromName ||
    raw.type ||
    'application/octet-stream'
  if (raw.type === type) return raw
  const buf = await raw.arrayBuffer()
  return new Blob([buf], { type })
}

export type SpreadsheetPreviewPage = {
  sheet_names: string[]
  sheet_index: number
  sheet_name: string
  header: string[]
  rows: string[][]
  offset: number
  limit: number
  total_data_rows: number
  has_more: boolean
  truncated_by_config: boolean
  max_rows_per_sheet: number
  method: string
  original_name?: string
}

export async function fetchSpreadsheetPreviewPage(
  itemId: number,
  rec: FileRec,
  scopeParams: AttachmentScopeParams,
  params: { sheet_index?: number; offset?: number; limit?: number },
): Promise<SpreadsheetPreviewPage> {
  const stored = rec.stored_name
  if (!stored) throw new Error('附件无效')
  const res = await adminApi.get<SpreadsheetPreviewPage>(
    `/admin/knowledge/items/${itemId}/attachment/${stored}/preview-sheet`,
    {
      params: {
        ...scopeParams,
        sheet_index: params.sheet_index ?? 0,
        offset: params.offset ?? 0,
        limit: params.limit ?? 200,
      },
    },
  )
  return res.data
}

export async function fetchAttachmentPreviewDocx(
  itemId: number,
  rec: FileRec,
  scopeParams: AttachmentScopeParams,
): Promise<Blob> {
  const stored = rec.stored_name
  if (!stored) throw new Error('附件无效')
  const res = await adminApi.get(
    `/admin/knowledge/items/${itemId}/attachment/${stored}/preview-docx`,
    { params: scopeParams, responseType: 'blob' },
  )
  return res.data as Blob
}

export async function fetchAttachmentPreviewText(
  itemId: number,
  rec: FileRec,
  scopeParams: AttachmentScopeParams,
): Promise<string> {
  const stored = rec.stored_name
  if (!stored) throw new Error('附件无效')
  const res = await adminApi.get<{ text: string }>(
    `/admin/knowledge/items/${itemId}/attachment/${stored}/preview-text`,
    { params: scopeParams },
  )
  return res.data.text || ''
}

/** iframe 预览用同源 URL（Chrome 对 blob: PDF 常空白；iframe 无法带 Authorization 头） */
export function attachmentContentPreviewUrl(
  itemId: number,
  rec: FileRec,
  scopeParams: AttachmentScopeParams,
): string {
  const stored = rec.stored_name
  if (!stored) throw new Error('附件无效')
  const params = new URLSearchParams({ scope: scopeParams.scope })
  const token = getAdminToken()
  if (token) params.set('access_token', token)
  const name = encodeURIComponent(stored)
  return `/api/admin/knowledge/items/${itemId}/attachment/${name}/content?${params.toString()}`
}

/** 经后端代理读取附件（避免浏览器直连 OSS 触发 CORS Failed to fetch） */
export async function fetchAttachmentBlob(itemId: number, rec: FileRec, scopeParams: AttachmentScopeParams) {
  const stored = rec.stored_name
  if (!stored) throw new Error('附件无效')

  const res = await adminApi.get(
    `/admin/knowledge/items/${itemId}/attachment/${stored}/content`,
    { params: scopeParams, responseType: 'blob' },
  )
  const blob = await normalizeResponseBlob(
    res.data as Blob,
    rec,
    String(res.headers?.['content-type'] || ''),
  )
  const url = URL.createObjectURL(blob)
  return { blob, url }
}

export async function renderDocxHtml(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer()
  const result = await mammoth.convertToHtml({ arrayBuffer: buf })
  return result.value
}
